import { formatIDR, formatNumber } from './format';

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export function toUserFriendlyErrorMessage(
  error: unknown,
  fallback = DEFAULT_ERROR_MESSAGE,
): string {
  const rawMessage = readErrorMessage(error);
  if (!rawMessage) return fallback;

  const normalized = normalizeErrorMessage(rawMessage);
  if (!normalized) return fallback;

  const mappedMessage = mapKnownErrorMessage(normalized);
  if (mappedMessage) return mappedMessage;

  if (looksTechnical(normalized)) return fallback;

  return normalized;
}

function readErrorMessage(error: unknown): string | null {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }

  return null;
}

function normalizeErrorMessage(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) return '';

  const markers = [
    'Uncaught ConvexError:',
    'Uncaught Error:',
    'ConvexError:',
    'Error:',
  ];

  for (const marker of markers) {
    const index = trimmed.lastIndexOf(marker);
    if (index >= 0) {
      return cleanupMessage(trimmed.slice(index + marker.length));
    }
  }

  const lines = trimmed
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && line !== 'Server Error' && !line.startsWith('[CONVEX '));

  const candidate =
    [...lines].reverse().find((line) => !line.startsWith('at ') && !line.includes('stack')) ??
    trimmed;

  return cleanupMessage(candidate);
}

function cleanupMessage(message: string): string {
  return message
    .replace(/^Server Error\s*/i, '')
    .replace(/^(Uncaught\s+)?(ConvexError|Error):\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function mapKnownErrorMessage(message: string): string | null {
  const lower = message.toLowerCase();

  if (
    /invalid (password|credentials)/i.test(message) ||
    /incorrect (email|password)/i.test(message) ||
    /wrong password/i.test(message)
  ) {
    return 'Your email or password is incorrect.';
  }

  if (
    /already exists/i.test(message) ||
    /already registered/i.test(message) ||
    /already in use/i.test(message)
  ) {
    return 'That email is already registered. Sign in instead or use a different email.';
  }

  if (
    lower.includes('logged in') ||
    lower.includes('not authenticated') ||
    lower.includes('authentication required')
  ) {
    return 'Please sign in to continue.';
  }

  if (
    lower.includes('s3 configuration not set') ||
    lower.startsWith('failed to upload:')
  ) {
    return 'We could not upload that image right now. Try another file or use an image URL.';
  }

  if (
    lower.includes('admin privileges') ||
    lower.includes('unauthorized') ||
    lower.includes('forbidden') ||
    lower.includes('permission denied')
  ) {
    return 'You do not have permission to do that.';
  }

  if (
    lower.includes('networkerror') ||
    lower.includes('failed to fetch') ||
    lower.includes('fetch failed') ||
    lower.includes('load failed') ||
    lower.includes('network request failed')
  ) {
    return 'We could not reach the server. Check your connection and try again.';
  }

  const bidTooLowMatch = message.match(
    /Bid of (\d+) rp is too low\. Minimum next bid is (\d+) rp\./i,
  );
  if (bidTooLowMatch) {
    const [, , minimumBid] = bidTooLowMatch;
    return `Your bid is too low. The next valid bid is ${formatIDR(Number(minimumBid))}.`;
  }

  const insufficientFundsMatch = message.match(
    /Insufficient funds: you have (\d+) rp but need (\d+) rp\./i,
  );
  if (insufficientFundsMatch) {
    const [, available, required] = insufficientFundsMatch;
    return `You do not have enough balance for this bid. Available: ${formatIDR(Number(available))}. Required: ${formatIDR(Number(required))}.`;
  }

  const sandboxCapMatch = message.match(/Sandbox top-up is capped at (\d+) RP per request\./i);
  if (sandboxCapMatch) {
    return `Sandbox top-ups are capped at ${formatNumber(Number(sandboxCapMatch[1]))} RP per request.`;
  }

  if (lower.includes('sellers cannot bid on their own auctions')) {
    return 'You cannot bid on your own listing.';
  }

  if (lower.includes('already the highest bidder')) {
    return 'You are already the highest bidder on this auction.';
  }

  return null;
}

function looksTechnical(message: string): boolean {
  return (
    /\b(TypeError|ReferenceError|SyntaxError|RangeError)\b/.test(message) ||
    /^Cannot\s+(read|set)\s+/i.test(message) ||
    message.includes('http://') ||
    message.includes('https://') ||
    /\bat\s+\S+\s*\(/.test(message)
  );
}
